import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import './Blog.css';

const BLOG_IMG_BASE = 'https://mansimehta107.github.io/greenwheels/blog/images/';

function fixSrc(src) {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  if (src.startsWith('../images/')) return BLOG_IMG_BASE + src.slice(10);
  return src;
}

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const currency = useCurrency();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/blog/${slug}`),
      api.get('/blog'),
      api.get('/products'),
    ])
      .then(([{ data: postData }, { data: allPosts }, { data: allProducts }]) => {
        setPost(postData);
        setRelated(allPosts.filter(p => p.slug !== slug).slice(0, 3));
        setProducts(allProducts);
      })
      .catch(() => navigate('/blog', { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <div className="blog-loading">Loading...</div>;
  if (!post) return null;

  const sections = normaliseSections(post);
  const splitRows    = sections.filter(s => s.type === 'split-row');
  const fullSections = sections.filter(s => s.type === 'full');
  const featuresBar  = sections.find(s => s.type === 'features');
  const ctaSection   = sections.find(s => s.type === 'cta');

  // Resolve a card's _productId to the live product object
  const resolveCard = (card) => {
    if (!card._productId) return card; // legacy scraped card — use stored fields
    return products.find(p => (p._id || p.id) === card._productId) || null;
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bd-breadcrumb">
        <div className="container">
          <Link to="/blog">Blog</Link>
          <span>›</span>
          <span>{post.category || post.title}</span>
        </div>
      </div>

      {/* Split Hero */}
      <div className="bd-hero-split">
        <div className="bd-hero-text">
          {post.category && <span className="bd-tag">{post.category} Gift Guide</span>}
          <h1>{post.title}</h1>
          <p>{post.excerpt}</p>
          <a href="/products" className="bd-cta-btn">Browse All Scooters</a>
        </div>
        <div className="bd-hero-image">
          <img src={getImageUrl(post.image)} alt={post.title}
            onError={e => { e.target.src = '/images/scooter-1.jpg'; }} />
        </div>
      </div>

      {/* Alternating split rows */}
      {splitRows.length > 0 && (
        <div className="bd-content">
          {splitRows.map((row, i) => (
            <div key={i} className={`bd-split-row${i % 2 === 1 ? ' reverse' : ''}`}>
              <div className={`bd-split-image${row.productImg ? ' product-img' : ''}`}>
                <img
                  src={row.image ? fixSrc(row.image) : getImageUrl(post.image)}
                  alt={row.heading}
                  onError={e => { e.target.src = '/images/scooter-1.jpg'; }}
                />
              </div>
              <div className="bd-split-text">
                <h2>{row.heading}</h2>
                {row.body.map((item, j) => {
                  if (item.type === 'paragraph' || item.type === 'p')
                    return <p key={j} dangerouslySetInnerHTML={{ __html: item.value || item.html || '' }} />;
                  if (item.type === 'list' || item.type === 'ul') {
                    const items = item.items || (item.value || '').split('\n').filter(Boolean);
                    return <ul key={j}>{items.map((li, k) => <li key={k} dangerouslySetInnerHTML={{ __html: li }} />)}</ul>;
                  }
                  return null;
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product card sections */}
      {fullSections.map((sec, i) => (
        <div key={i} className="bd-content">
          <div className="bd-full-section">
            {sec.heading && <h2>{sec.heading}</h2>}
            {sec.subtext && <p>{sec.subtext}</p>}
            <div className="bd-product-cards">
              {(sec.cards || []).map((card, j) => {
                const prod = resolveCard(card);
                if (!prod) return null;

                // Determine values — live product data takes priority
                const name     = prod.name;
                const specs    = prod.range || '';
                const price    = prod.price;
                const oldPrice = prod.oldPrice;
                const image    = prod.image || (prod.images && prod.images[0]) || '';
                const category = prod.category || 'electric-scooters';
                const link     = prod.model ? `/products/${category}/${prod.model}` : '/products';

                return (
                  <div key={j} className="bd-product-card">
                    <img src={getImageUrl(image)} alt={name}
                      onError={e => { e.target.src = '/images/scooter-1.jpg'; }} />
                    <div className="bd-card-body">
                      <h4>{name}</h4>
                      {specs && <p className="bd-specs">{specs}</p>}
                      <div className="bd-price">
                        <span className="bd-price-current">{currency}{price}</span>
                        {oldPrice > 0 && <span className="bd-price-old">{currency}{oldPrice}</span>}
                      </div>
                      <a href={link} className="bd-btn-shop">View Scooter</a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Features bar */}
      {featuresBar && (
        <div className="bd-features-bar">
          <div className="container">
            <h2>{featuresBar.heading || 'Why Choose Green Wheels'}</h2>
            <div className="bd-features-grid">
              {(featuresBar.items || []).map((f, i) => (
                <div key={i} className="bd-feature-item">
                  {f.icon && <span className="bd-feature-icon">{f.icon}</span>}
                  <h4>{f.heading}</h4>
                  <p>{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="bd-cta-section">
        <h2>{ctaSection?.heading || 'Ready to Find the Perfect Gift?'}</h2>
        <p>{ctaSection?.text || 'Browse our full range of electric scooters and e-bikes, with free delivery across Ireland and a 1-year warranty.'}</p>
        <a href="/products" className="bd-cta-btn">Shop All Scooters</a>
        <a href="/contact" className="bd-cta-btn secondary">Contact Us</a>
      </div>

      {/* Related posts */}
      {related.length > 0 && (
        <div className="container bd-related">
          <h3>More Gift Guides</h3>
          <div className="blog-grid blog-grid-3">
            {related.map(p => (
              <article key={p._id} className="blog-card">
                <Link to={`/blog/${p.slug}`} className="blog-card-img-wrap">
                  <img src={getImageUrl(p.image)} alt={p.title}
                    onError={e => { e.target.src = '/images/scooter-1.jpg'; }} />
                  {p.category && <span className="blog-card-cat">{p.category}</span>}
                </Link>
                <div className="blog-card-body">
                  <h2 className="blog-card-title"><Link to={`/blog/${p.slug}`}>{p.title}</Link></h2>
                  <p className="blog-card-excerpt">{p.excerpt}</p>
                  <Link to={`/blog/${p.slug}`} className="blog-read-more">Read More <span>→</span></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function normaliseSections(post) {
  if (post.sections && post.sections.length > 0) return post.sections;
  if (!post.content) return [];

  const doc = new DOMParser().parseFromString(post.content, 'text/html');
  const sections = [];

  doc.querySelectorAll('.split-row').forEach(row => {
    const imgEl = row.querySelector('.split-image img');
    const isProduct = !!row.querySelector('.split-image.product-img');
    const heading = row.querySelector('.split-text h2')?.textContent?.trim() || '';
    const body = [];
    row.querySelectorAll('.split-text > p, .split-text > ul').forEach(el => {
      if (el.tagName === 'P') body.push({ type: 'p', html: el.innerHTML });
      else if (el.tagName === 'UL')
        body.push({ type: 'ul', items: [...el.querySelectorAll('li')].map(li => li.innerHTML) });
    });
    sections.push({ type: 'split-row', heading, image: imgEl?.getAttribute('src') || '', productImg: isProduct, body });
  });

  doc.querySelectorAll('.full-section').forEach(sec => {
    const heading = sec.querySelector('h2')?.textContent?.trim() || '';
    const subtext = sec.querySelector(':scope > p')?.textContent?.trim() || '';
    const cards = [...sec.querySelectorAll('.product-card')].map(card => ({
      name:     card.querySelector('h4')?.textContent?.trim() || '',
      specs:    card.querySelector('.specs')?.textContent?.trim() || '',
      price:    card.querySelector('.price .current')?.textContent?.trim() || '',
      oldPrice: card.querySelector('.price .original')?.textContent?.trim() || '',
      image:    card.querySelector('img')?.getAttribute('src') || '',
      link:     card.querySelector('.btn-shop')?.getAttribute('href') || '',
    }));
    if (cards.length) sections.push({ type: 'full', heading, subtext, cards });
  });

  const featBar = doc.querySelector('.features-bar');
  if (featBar) {
    sections.push({
      type: 'features',
      heading: featBar.querySelector('h2')?.textContent?.trim() || 'Why Choose Green Wheels',
      items: [...featBar.querySelectorAll('.feature-item')].map(f => ({
        icon:    f.querySelector('.icon')?.textContent?.trim() || '',
        heading: f.querySelector('h4')?.textContent?.trim() || '',
        text:    f.querySelector('p')?.textContent?.trim() || '',
      })),
    });
  }

  const cta = doc.querySelector('.cta-section');
  if (cta) {
    sections.push({
      type:    'cta',
      heading: cta.querySelector('h2')?.textContent?.trim() || '',
      text:    cta.querySelector('p')?.textContent?.trim() || '',
    });
  }

  return sections;
}
